/**
 * Organization Dashboard
 *
 * Admin dashboard for managing organizations, users, and impersonation.
 * Only accessible to Ailyce admins.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Tab,
  Tabs,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import organizationService from '../services/organizationService';
import OrganizationList from '../components/admin/OrganizationList';
import OrganizationStats from '../components/admin/OrganizationStats';
import CreateOrganizationDialog from '../components/admin/CreateOrganizationDialog';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function OrganizationDashboard() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load stats and organizations in parallel
      const [statsData, orgsData] = await Promise.all([
        organizationService.getOrganizationStats(),
        organizationService.listOrganizations()
      ]);

      setStats(statsData);
      setOrganizations(orgsData);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to access this page. Ailyce admin access required.');
      } else {
        setError('Failed to load organization data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreateOrganization = async (data) => {
    try {
      await organizationService.createOrganization(data);
      setCreateDialogOpen(false);
      await loadData(); // Reload data
    } catch (err) {
      console.error('Error creating organization:', err);
      throw err;
    }
  };

  const handleOrganizationClick = (orgId) => {
    navigate(`/admin/organizations/${orgId}`);
  };

  if (loading && !stats) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading organization data...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ 
          textTransform: 'none !important'
         }}>
          Go to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon fontSize="large" />
            Organization Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage organizations, users, and system access
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleRefresh} color="primary" disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Organization
          </Button>
        </Box>
      </Box>

      {/* Stats Overview */}
      {stats && <OrganizationStats stats={stats} />}

      {/* Tabs */}
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="All Organizations" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Active" icon={<Chip label={stats?.active_organizations || 0} size="small" color="success" />} iconPosition="end" />
          <Tab label="Suspended" icon={<Chip label={stats?.suspended_organizations || 0} size="small" color="warning" />} iconPosition="end" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <OrganizationList
            organizations={organizations}
            onOrganizationClick={handleOrganizationClick}
            onRefresh={loadData}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <OrganizationList
            organizations={organizations.filter(org => org.status === 'active')}
            onOrganizationClick={handleOrganizationClick}
            onRefresh={loadData}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <OrganizationList
            organizations={organizations.filter(org => org.status === 'suspended')}
            onOrganizationClick={handleOrganizationClick}
            onRefresh={loadData}
          />
        </TabPanel>
      </Paper>

      {/* Create Organization Dialog */}
      <CreateOrganizationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateOrganization}
      />
    </Container>
  );
}
